import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDate, formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Textarea,
  confirm,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { CampRegistrationStatus } from "@/server/platform/db/schema/camp-registrations";
import { canTransitionCampRegistrationStatus } from "../../domain/lib/status-transitions";
import {
  ArrowLeftIcon,
  CalendarIcon,
  MessageCircleIcon,
  PackageIcon,
  PhoneIcon,
  RotateCcwIcon,
  SparklesIcon,
  UserIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { campRegistrationsApi } from "../api/client";
import type { CampSimilarStudentMatchDTO } from "../api/client";
import {
  useCampRegistrationQuery,
  useCampSimilarStudentsQuery,
  useConvertCampRegistrationMutation,
  useUpdateCampRegistrationMutation,
} from "../api/queries";
import {
  CAMP_GENDER_LABELS,
  CAMP_REGISTRATION_STATUS_LABELS,
  CAMP_REGISTRATION_STATUS_VARIANTS,
} from "../lib/camp-status";
import {
  CampPackagePickerDialog,
  type CampSellPackageOption,
} from "../ui/CampPackagePickerDialog";
import { buildCampRegistrationWhatsAppUrl } from "../../domain/lib/whatsapp";

const ACTION_STATUSES = [
  "contacted",
  "confirmed",
  "rejected",
  "duplicate",
] as const;

const STATUS_CONFIRM_CONFIG: Record<
  (typeof ACTION_STATUSES)[number],
  {
    title: string;
    description: string;
    actionText: string;
    destructive?: boolean;
  }
> = {
  contacted: {
    title: "ຕິດຕໍ່ແລ້ວ",
    description: "ຢືນຢັນວ່າທີມງານຕິດຕໍ່ຜູ້ສະໝັກແລ້ວ?",
    actionText: "ຕິດຕໍ່ແລ້ວ",
  },
  confirmed: {
    title: "ຢືນຢັນແລ້ວ",
    description: "ຢືນຢັນວ່າຜູ້ສະໝັກຢືນຢັນລົງທະບຽນແລ້ວ?",
    actionText: "ຢືນຢັນແລ້ວ",
  },
  rejected: {
    title: "ປະຕິເສດໃບສະໝັກ",
    description: "ຢືນຢັນປະຕິເສດໃບສະໝັກນີ້? ການກະທຳນີ້ບໍ່ສາມາດຍ້ອນກັບໄດ້.",
    actionText: "ປະຕິເສດ",
    destructive: true,
  },
  duplicate: {
    title: "ໝາຍວ່າຊ້ຳ",
    description: "ຢືນຢັນວ່າໃບສະໝັກນີ້ຊ້ຳກັບຄົນອື່ນ?",
    actionText: "ໝາຍວ່າຊ້ຳ",
    destructive: true,
  },
};

export function CampRegistrationDetailPage() {
  const { id } = useParams({ from: "/app/camp-registrations/$id" });
  const nav = useNavigate({ from: "/app/camp-registrations/$id" });
  const { data: reg, isLoading } = useCampRegistrationQuery(id);
  const updateMutation = useUpdateCampRegistrationMutation();
  const convertMutation = useConvertCampRegistrationMutation();
  const canUpdate = useActionPermission(["camp-registrations:update"]);
  const canConvert = useActionPermission(["camp-registrations:convert"]);
  const canSellPackage = useActionPermission(["packages:sell"]);

  const [adminNotes, setAdminNotes] = useState("");
  const [linkStudentId, setLinkStudentId] = useState<string | null>(null);
  const [packagePickerOpen, setPackagePickerOpen] = useState(false);
  const [packagePickerOptions, setPackagePickerOptions] = useState<
    CampSellPackageOption[]
  >([]);
  const [packagePickerStudentId, setPackagePickerStudentId] = useState("");

  const detail = reg;

  const canRunConvert =
    !!detail &&
    (detail.status === "confirmed" || detail.status === "contacted") &&
    !detail.studentId;

  const { data: similarStudentsData, isLoading: similarStudentsLoading } =
    useCampSimilarStudentsQuery(id, canRunConvert && canConvert);

  const similarMatches = similarStudentsData?.matches ?? [];

  useEffect(() => {
    if (!detail) return;
    setAdminNotes(detail.adminNotes ?? "");
  }, [detail?.adminNotes, detail?.id]);

  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
        { label: "ໃບສະໝັກ Camp", to: "/app/camp-registrations" },
        { label: "ລາຍລະອຽດ" },
      ]}
    />
  );

  if (isLoading && !detail) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <div className="flex items-center justify-center py-24">
            <div className="text-muted-foreground">ກຳລັງໂຫຼດ...</div>
          </div>
        </Main>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <p className="py-12 text-center text-muted-foreground">
            ບໍ່ພົບໃບສະໝັກ
          </p>
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/camp-registrations" })}
            >
              ກັບໄປລາຍການ
            </Button>
          </div>
        </Main>
      </>
    );
  }

  const genderLabel =
    detail.gender === "other"
      ? detail.genderOther?.trim() || CAMP_GENDER_LABELS.other
      : CAMP_GENDER_LABELS[detail.gender] ?? detail.gender;

  const whatsappUrl = buildCampRegistrationWhatsAppUrl({
    phoneNumber: detail.phoneNumber,
    fullName: detail.fullName,
    nickname: detail.nickname,
    eventTitle: detail.event?.title,
  });

  const currentStatus = detail.status as CampRegistrationStatus;
  const availableStatusActions = ACTION_STATUSES.filter(
    (status) =>
      canTransitionCampRegistrationStatus(currentStatus, status) &&
      status !== currentStatus,
  );
  const canRevertToPending =
    currentStatus !== "pending" &&
    canTransitionCampRegistrationStatus(currentStatus, "pending");

  const handleStatusChange = async (
    status: (typeof ACTION_STATUSES)[number],
  ) => {
    const confirmConfig = STATUS_CONFIRM_CONFIG[status];
    const ok = await confirm({
      title: confirmConfig.title,
      description: `${confirmConfig.description}\n\n${detail.fullName} (${detail.nickname})`,
      actionText: confirmConfig.actionText,
      ActionProps: confirmConfig.destructive
        ? { variant: "destructive" }
        : undefined,
    });
    if (!ok) return;

    try {
      await updateMutation.mutateAsync({
        id,
        body: { status, adminNotes: adminNotes || null },
      });
      toast.success("ອັບເດດສະຖານະສຳເລັດ");
    } catch {
      toast.error("ອັບເດດສະຖານະລົ້ມເຫຼວ");
    }
  };

  const handleRevertToPending = async () => {
    const ok = await confirm({
      title: "ຍ້ອນກັບເປັນລໍຖ້າຕິດຕໍ່",
      description: `ຢືນຢັນຍ້ອນສະຖານະໃບສະໝັກນີ້ກັບເປັນ «ລໍຖ້າຕິດຕໍ່»?\n\n${detail.fullName} (${detail.nickname})`,
      actionText: "ຍ້ອນກັບ",
    });
    if (!ok) return;

    try {
      await updateMutation.mutateAsync({
        id,
        body: { status: "pending", adminNotes: adminNotes || null },
      });
      toast.success("ຍ້ອນສະຖານະເປັນລໍຖ້າຕິດຕໍ່ສຳເລັດ");
    } catch {
      toast.error("ຍ້ອນສະຖານະລົ້ມເຫຼວ");
    }
  };

  const navigateToSellWithTemplate = (
    registrationId: string,
    studentId: string,
    template: CampSellPackageOption,
  ) => {
    nav({
      to: "/app/sell",
      search: {
        type: "package",
        studentId,
        templateId: template.templateId,
        campRegistrationId: registrationId,
        selectedRuleIds: template.selectedRuleIds.join(","),
      },
    });
  };

  const openSellCampPackageFlow = async (registrationId: string) => {
    const options = await campRegistrationsApi.getSellPackageOptions(
      registrationId,
    );

    if (options.templates.length === 1) {
      const only = options.templates[0]!;
      navigateToSellWithTemplate(
        registrationId,
        options.studentId,
        only,
      );
      return;
    }

    setPackagePickerStudentId(options.studentId);
    setPackagePickerOptions(options.templates);
    setPackagePickerOpen(true);
  };

  const handlePackagePickerSelect = (templateId: string) => {
    const template = packagePickerOptions.find(
      (item) => item.templateId === templateId,
    );
    if (!template || !packagePickerStudentId) return;
    navigateToSellWithTemplate(id, packagePickerStudentId, template);
  };

  const handleSellCampPackage = async () => {
    try {
      await openSellCampPackageFlow(id);
    } catch {
      toast.error(
        "ບໍ່ສາມາດເປີດຂາຍແພັກເກດ — ຕັ້ງແພັກເກດ Camp ໃນ Summer Camp config ກ່ອນ",
      );
    }
  };

  const runConvert = async (body: {
    linkExistingStudentId?: string;
    forceCreateNew?: boolean;
  }) => {
    await convertMutation.mutateAsync({ id, body });
    toast.success("ແປງເປັນນັກຮຽນສຳເລັດ");
    try {
      await openSellCampPackageFlow(id);
      toast.info("ເປີດຫນ້າຂາຍແພັກເກດ Camp — ກະລຸນາຢືນຢັນ ແລະ ບັນທຶກ");
    } catch {
      toast.warning(
        "ແປງສຳເລັດແລ້ວ — ຕັ້ງແພັກເກດ Camp ໃນ /app/camp-events ກ່ອນຂາຍ",
      );
      nav({ to: "/app/camp-registrations" });
    }
  };

  const handleConvert = async () => {
    try {
      if (
        similarMatches.length > 0 &&
        !linkStudentId
      ) {
        const ok = await confirm({
          title: "ສ້າງນັກຮຽນໃໝ່?",
          description:
            `ພົບນັກຮຽນທີ່ຄ້າຍກັນ ≥70% ຢູ່ ${similarMatches.length} ຄົນ — ຢືນຢັນວ່າຕ້ອງການສ້າງນັກຮຽນໃໝ່ແທນການຜູກກັບຄົນເກົ່າ?`,
          actionText: "ສ້າງໃໝ່",
          ActionProps: { variant: "destructive" },
        });
        if (!ok) return;
        await runConvert({ forceCreateNew: true });
        return;
      }

      await runConvert({
        linkExistingStudentId: linkStudentId ?? undefined,
      });
    } catch (e: unknown) {
      const err = e as Error & {
        code?: string;
        matches?: CampSimilarStudentMatchDTO[];
      };
      if (err.matches?.length) {
        setLinkStudentId(err.matches[0]?.id ?? null);
        toast.error("ພົບນັກຮຽນຄ້າຍກັນ — ເລືອກຜູກກັບນັກຮຽນທີ່ມີຢູ່ ຫຼື ຢືນຢັນສ້າງໃໝ່");
        return;
      }
      toast.error("ແປງເປັນນັກຮຽນລົ້ມເຫຼວ");
    }
  };

  return (
    <>
      <Header>{breadcrumb}</Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-bold text-2xl">ລາຍລະອຽດໃບສະໝັກ Camp</h1>
              <Badge
                variant={
                  CAMP_REGISTRATION_STATUS_VARIANTS[detail.status] ?? "outline"
                }
              >
                {CAMP_REGISTRATION_STATUS_LABELS[detail.status] ??
                  detail.status}
              </Badge>
            </div>
            <p className="mt-1 font-mono text-muted-foreground text-sm">
              ID: {detail.id}
            </p>
            {detail.event?.title ? (
              <p className="mt-1 text-muted-foreground text-sm">
                {detail.event.title}
              </p>
            ) : null}
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/camp-registrations" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບໄປລາຍການ
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                  <UserIcon className="size-4" />
                  ຂໍ້ມູນຜູ້ສະໝັກ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ຊື່–ນາມສະກຸນ
                      </p>
                      <p className="font-semibold">{detail.fullName}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ຊື່ຫຼິ້ນ
                      </p>
                      <p className="font-semibold">{detail.nickname}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ເພດ
                      </p>
                      <p className="font-semibold">{genderLabel}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-2">
                      <PhoneIcon className="mt-0.5 size-4 text-muted-foreground" />
                      <div className="min-w-0 flex-1 space-y-2">
                        <div>
                          <p className="font-medium text-muted-foreground text-xs uppercase">
                            ເບີໂທ
                          </p>
                          <p className="font-semibold">{detail.phoneNumber}</p>
                        </div>
                        {whatsappUrl ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400"
                            onClick={() =>
                              window.open(whatsappUrl, "_blank", "noopener,noreferrer")
                            }
                          >
                            <MessageCircleIcon className="size-4" />
                            ຕິດຕໍ່ WhatsApp
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <CalendarIcon className="mt-0.5 size-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium text-muted-foreground text-xs uppercase">
                          ວັນເດືອນປີເກີດ
                        </p>
                        <p className="font-semibold">
                          {formatDate(detail.dateOfBirth)}
                          {detail.age != null ? ` (${detail.age} ປີ)` : ""}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground text-xs uppercase">
                        ວັນທີສະໝັກ
                      </p>
                      <p className="font-semibold">
                        {formatDateTime(detail.submittedAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-normal text-muted-foreground text-sm">
                  <SparklesIcon className="size-4" />
                  ຄລາສທີ່ເລືອກ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {detail.classes.map((item) => (
                    <Badge key={item.classTypeId} variant="secondary">
                      {item.name}
                    </Badge>
                  ))}
                </div>
                <p className="mt-4 text-muted-foreground text-sm">
                  Preview ນັກຮຽນ:{" "}
                  <span className="font-medium text-foreground">
                    {detail.previewStudentName}
                  </span>
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="font-normal text-muted-foreground text-sm">
                  ຈັດການໃບສະໝັກ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="adminNotes">ໝາຍເຫດທີມງານ</Label>
                  <Textarea
                    id="adminNotes"
                    placeholder="ບັນທຶກການຕິດຕໍ່ ຫຼື ຂໍ້ມູນເພີ່ມ..."
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    disabled={!canUpdate}
                  />
                </div>

                {canUpdate ? (
                  <div className="space-y-3">
                    {availableStatusActions.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {availableStatusActions.map((status) => (
                          <Button
                            key={status}
                            size="sm"
                            variant="outline"
                            disabled={updateMutation.isPending}
                            onClick={() => handleStatusChange(status)}
                          >
                            {CAMP_REGISTRATION_STATUS_LABELS[status] ?? status}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        ບໍ່ມີການປ່ຽນສະຖານະຖັດໄປຈາກສະຖານະນີ້
                      </p>
                    )}
                    {canRevertToPending ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        disabled={updateMutation.isPending}
                        onClick={handleRevertToPending}
                      >
                        <RotateCcwIcon className="size-4" />
                        ຍ້ອນກັບເປັນລໍຖ້າຕິດຕໍ່
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                {canRunConvert && canConvert ? (
                  <div className="space-y-3 border-t pt-4">
                    <p className="font-medium text-sm">ແປງເປັນນັກຮຽນ</p>
                    {similarStudentsLoading ? (
                      <p className="text-muted-foreground text-xs">
                        ກຳລັງກວດຫານັກຮຽນຄ້າຍກັນ...
                      </p>
                    ) : null}
                    {similarMatches.length > 0 ? (
                      <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                        <p className="text-amber-700 text-xs dark:text-amber-400">
                          ພົບນັກຮຽນຄ້າຍກັນ ≥70% — ເລືອກຜູກກັບນັກຮຽນທີ່ມີຢູ່
                          ຫຼື ກົດ Convert ເພື່ອສ້າງໃໝ່
                        </p>
                        {similarMatches.map((match) => (
                          <Button
                            key={match.id}
                            type="button"
                            size="sm"
                            variant={
                              linkStudentId === match.id ? "default" : "outline"
                            }
                            className="h-auto w-full justify-start py-2"
                            onClick={() =>
                              setLinkStudentId(
                                linkStudentId === match.id ? null : match.id,
                              )
                            }
                          >
                            <span className="flex w-full flex-col items-start gap-0.5 text-left">
                              <span>{match.name}</span>
                              <span className="font-normal text-muted-foreground text-xs">
                                {match.memberId}
                                {match.phoneNumber
                                  ? ` · ${match.phoneNumber}`
                                  : ""}
                                {` · ຄ້າຍ ${match.similarityPercent}%`}
                              </span>
                            </span>
                          </Button>
                        ))}
                      </div>
                    ) : !similarStudentsLoading ? (
                      <p className="text-muted-foreground text-xs">
                        ບໍ່ພົບນັກຮຽນຄ້າຍກັນ ≥70%
                      </p>
                    ) : null}
                    <Button
                      className="w-full"
                      disabled={convertMutation.isPending || similarStudentsLoading}
                      onClick={handleConvert}
                    >
                      {linkStudentId
                        ? "ຜູກກັບນັກຮຽນທີ່ມີຢູ່"
                        : "Convert to Student"}
                    </Button>
                  </div>
                ) : null}

                {detail.studentId && canSellPackage && !detail.studentPackageId ? (
                  <Button
                    className="w-full"
                    onClick={handleSellCampPackage}
                  >
                    <PackageIcon className="size-4" />
                    ຂາຍ Camp Package
                  </Button>
                ) : null}

                {detail.studentPackageId ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      nav({
                        to: "/app/students/$id",
                        params: { id: detail.studentId! },
                      })
                    }
                  >
                    <PackageIcon className="size-4" />
                    ເບິ່ງແພັກເກດ Camp
                  </Button>
                ) : null}

                {detail.studentId ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      nav({
                        to: "/app/students/$id",
                        params: { id: detail.studentId! },
                      })
                    }
                  >
                    ເບິ່ງນັກຮຽນ
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </div>
      </Main>

      <CampPackagePickerDialog
        open={packagePickerOpen}
        onOpenChange={setPackagePickerOpen}
        templates={packagePickerOptions}
        onSelect={handlePackagePickerSelect}
      />
    </>
  );
}
